import { useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import { useSearchParams } from 'hooks'

import type { IUseBrandHiddenLegendParams, IUseBrandHiddenLegendReturn } from './use-brand-hidden-legend.types'

export function useBrandHiddenLegend({
  validTerritoryBrands,
  brandOwnerId,
  hiddenBrandIds,
}: IUseBrandHiddenLegendParams): IUseBrandHiddenLegendReturn {
  const history = useHistory()
  const searchParams = useSearchParams()

  const hiddenBrandNames = useMemo(() => {
    const hiddenIds = new Set(hiddenBrandIds)
    return new Set(validTerritoryBrands.filter(b => hiddenIds.has(b.id)).map(b => b.name))
  }, [hiddenBrandIds, validTerritoryBrands])

  const ownerName = useMemo(
    () => validTerritoryBrands.find(b => b.id === brandOwnerId)?.name,
    [validTerritoryBrands, brandOwnerId]
  )

  const handleLegendItemClick = useCallback(
    ({ name }: { name: string }) => {
      if (!brandOwnerId) return

      const brand = validTerritoryBrands.find(b => b.name === name)
      if (!brand) return
      if (brand.id === brandOwnerId) return

      const currentHidden = searchParams.getAll('hiddenBrand')
      const hiddenSet = new Set(currentHidden)

      if (hiddenSet.has(brand.id)) {
        hiddenSet.delete(brand.id)
      } else {
        hiddenSet.add(brand.id)
      }

      const newSearchParams = new URLSearchParams(searchParams.toString())
      newSearchParams.delete('hiddenBrand')
      hiddenSet.forEach(id => newSearchParams.append('hiddenBrand', id))
      history.push({ search: newSearchParams.toString() })
    },
    [validTerritoryBrands, brandOwnerId, searchParams, history]
  )

  return {
    hiddenBrandNames,
    ownerName,
    handleLegendItemClick,
  }
}
